import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { Assignment as AssignmentIcon } from '@mui/icons-material';
import { ticketService } from '../../services/ticketService';
import { Ticket } from '../../types';
import ChamadosInfoView from './ChamadosInfoView';

export interface ChamadosRankingListaFilters {
  cliente_id?: number;
  atribuido_para?: number;
  chamado_assunto_id?: number;
  data_inicio?: string;
  data_fim?: string;
}

interface ChamadosRankingListaDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  filters: ChamadosRankingListaFilters;
}

const getStatusLabel = (status: string) => {
  const labels: Record<string, string> = {
    aberto: 'Aberto',
    em_andamento: 'Em Andamento',
    resolvido: 'Resolvido',
    fechado: 'Fechado',
    cancelado: 'Cancelado',
  };
  return labels[status] || status;
};

const getStatusColor = (status: string): 'error' | 'warning' | 'success' | 'default' => {
  switch (status) {
    case 'aberto':
      return 'error';
    case 'em_andamento':
      return 'warning';
    case 'resolvido':
      return 'success';
    default:
      return 'default';
  }
};

const getPrioridadeColor = (prioridade: string): 'success' | 'info' | 'warning' | 'error' | 'default' => {
  switch (prioridade) {
    case 'baixa':
      return 'success';
    case 'normal':
      return 'info';
    case 'alta':
      return 'warning';
    case 'urgente':
      return 'error';
    default:
      return 'default';
  }
};

const formatDate = (dateString: string) => {
  try {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
};

const ChamadosRankingListaDialog: React.FC<ChamadosRankingListaDialogProps> = ({
  open,
  onClose,
  title,
  subtitle,
  filters,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;

    let ativo = true;
    const carregar = async () => {
      setLoading(true);
      try {
        const response = await ticketService.getAll({
          limit: 1000,
          filters: {
            status: 'todos',
            cliente_id: filters.cliente_id,
            atribuido_para: filters.atribuido_para,
            chamado_assunto_id: filters.chamado_assunto_id,
            data_inicio: filters.data_inicio,
            data_fim: filters.data_fim,
          },
        });
        if (!ativo) return;
        setTickets(response.success && response.data ? response.data : []);
      } catch {
        if (ativo) setTickets([]);
      } finally {
        if (ativo) setLoading(false);
      }
    };

    carregar();
    return () => {
      ativo = false;
    };
  }, [open, filters]);

  useEffect(() => {
    if (!open) {
      setTickets([]);
      setSelectedTicketId(null);
    }
  }, [open]);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ py: 1.25 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <AssignmentIcon color="primary" sx={{ fontSize: 20 }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={800} lineHeight={1.2}>
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="caption" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4} gap={1}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                Carregando chamados...
              </Typography>
            </Box>
          ) : tickets.length === 0 ? (
            <Box py={4} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                Nenhum chamado encontrado.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                      '& .MuiTableCell-head': {
                        fontWeight: 700,
                        fontSize: '0.68rem',
                        py: 0.75,
                      },
                    }}
                  >
                    <TableCell>ID</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Assunto</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Prioridade</TableCell>
                    <TableCell>Abertura</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tickets.map((ticket) => (
                    <TableRow
                      key={ticket.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setSelectedTicketId(ticket.id)}
                    >
                      <TableCell>
                        <Typography variant="caption" fontWeight={700} color="primary.main" fontFamily="monospace">
                          #{ticket.id.toString().padStart(5, '0')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" fontWeight={600} noWrap>
                          {ticket.cliente?.nome || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" noWrap>
                          {ticket.assunto?.nome || 'Sem assunto'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(ticket.status)}
                          color={getStatusColor(ticket.status)}
                          size="small"
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={ticket.prioridade}
                          color={getPrioridadeColor(ticket.prioridade)}
                          size="small"
                          sx={{ height: 20, fontSize: '0.65rem', textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(ticket.createdAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
            {tickets.length} chamado{tickets.length !== 1 ? 's' : ''}
          </Typography>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <ChamadosInfoView
        open={Boolean(selectedTicketId)}
        onClose={() => setSelectedTicketId(null)}
        ticketId={selectedTicketId}
      />
    </>
  );
};

export default ChamadosRankingListaDialog;
